import type { BindSheetOptions } from "./sheet-element.ts";
import { type Option } from "@tento-lona/sheets";
import { StructuralCoHide } from "@tento-lona/sheets/platform";
import { RowNetworkKey } from "@tento-lona/sheets";
import { SheetViewModel } from "@tento-lona";
import { Time } from "@tento-chrono";
import { nonnull } from "@tento-core/assert";
import { LiveData } from "@tento-core/livedata";
import { Tracing } from "@tento-core/tracing";
import { SheetLayout, SheetLayoutPort } from "@tento-ui/components/sheet/sheet-layout";
import { $$ } from "@tento-ui/fastdom";
import { SizeGate } from "@tento-ui/ui/size-gate";
import { SheetLayoutGate } from "./sheet-layout-gate.ts";
import { SheetBinder } from "./sheet-index-renderer.ts";
import type { Size } from "@tento-ui/layout-utils";
import type { SheetElementCore } from "./sheet-element-core.ts";
import type { CommittedRowGeometry } from "./sheet-element-state.ts";
import { SheetElementLayout } from "./sheet-element-layout.ts";

/**
 * The sheet element's sheet bind work (F11): wait for size and legend width,
 * resolve the layout, apply it in one write (header peek, scroll position,
 * content visibility, bind-generation commit) and record the committed row
 * geometry. Moved out of sheet-element.ts unchanged; the element's state is
 * read and written through host accessors when it runs.
 */

/**
 * What `bindSheetWork` reads on its sheet element (when it runs), and the one
 * object it writes: the committed row geometry it records when a bind
 * commits (this module owns `CommittedRowGeometry`).
 */
export type BindSheetWorkHost = {
  /** The element as every step reads it. */
  readonly core: SheetElementCore;
  /** Row geometry as of the last committed bind; written here only. */
  readonly committed: CommittedRowGeometry;
  /** The locked column index, if any. */
  readonly lockedIndex: Option<number>;
  /** Apply a resolved layout. */
  readonly applyResolvedLayout: (sheet: SheetViewModel.Surface<SheetBinder>, layout: SheetLayoutPort, p: SheetLayoutGate.Props, content: Option<SheetLayoutPort.Content>) => void;
  /** The layout gate. */
  readonly layoutGate: SheetLayoutGate;
  /** Seed the size gate from a measurement. */
  readonly seedSizeFromMeasurement: () => void;
  /** The size gate. */
  readonly sizeGate: SizeGate;
  /** Rebind the legend; resolves its width. */
  readonly rebindLegend: (sheetChanged: boolean) => Promise<number>;
  /** Width occluded on the left. */
  readonly leftOccludedWidthPx: LiveData<number>;
  /** Hold editor observers for a sheet change. */
  readonly holdEditorObservers: (signal: Option<AbortSignal>) => () => void;
  /** The layout element. */
  readonly root: SheetLayout;
  /** Whether the debug popover shows. */
  readonly shouldShowDebugPopover: Option<LiveData.Readable<boolean>>;
  /** Rebind fixed child y positions. */
  readonly rebindFixedChildYPositions: () => void;
};

/** Each row's committed offset and cell height, by its raw id (the co-hide's row geometry). */

export function rowGeometryOf(
  sheet: SheetViewModel.Surface<SheetBinder>,
): Map<RowNetworkKey, StructuralCoHide.RowGeometry> {
  const out = new Map<RowNetworkKey, StructuralCoHide.RowGeometry>();
  for (const [rowId, layout] of sheet.layout.rowLayout) {
    out.set(RowNetworkKey.of(rowId), {
      offsetPx: layout.offsetPx,
      cellHeightPx: layout.cellHeightPx,
    });
  }
  return out;
}

/** The element's `#bindSheetWork`, as a function of its host. */
export async function bindSheetWork(
  host: BindSheetWorkHost,
  sheet: SheetViewModel.Surface<SheetBinder>,
  options: BindSheetOptions,
): Promise<void> {
    const index = host.lockedIndex;
    const layout = host.core.layout;
    // Capture ownership before any size/legend/layout await. A resumed old
    // bind must not borrow the generation or controller of its successor.
    const revealGen = host.core.bindState.bindGeneration;
    const bindSignal = host.core.bindState.bindSettleController.signal;
    const isCurrent = () => !bindSignal.aborted && host.core.bindState.bindGeneration === revealGen;

    Tracing.info("ui")("core/sheet/bind, idx", index);
    let legendwidth: number;
    let size: Size;

    const applyLayout = (p: SheetLayoutGate.Props) => {
      host.applyResolvedLayout(sheet, layout, p, null);
    };

    // Hide legend during rebind on sheet change to prevent visible layout
    // shifts while bindNow modifies the legend DOM. Skip for data-only
    // refreshes to avoid a flash. legend-ready is restored in the
    // applyLayout mutate, so legend + cells appear in the same frame.
    if (options.sheetChanged) {
      host.core.element.toggleAttribute("legend-ready", false);
    }

    // Unwire onChange during bindSheet to prevent double-fire: setLegendWidth
    // would trigger onChange (wired by a previous bindSheet), then we'd also
    // queue an explicit mutate — producing two identical applyLayout calls.
    host.layoutGate.onChange = null;
    host.seedSizeFromMeasurement();
    if (options.rebindLegend) {
      size = await host.sizeGate.waitForSize();
      if (!isCurrent()) return;
      legendwidth = await host.rebindLegend(options.sheetChanged);
      if (!isCurrent()) return;
      host.layoutGate.setLegendWidth(legendwidth);
    } else {
      legendwidth = host.leftOccludedWidthPx.get();
      size = await host.sizeGate.waitForSize();
      if (!isCurrent()) return;
      host.layoutGate.setLegendWidth(legendwidth);
    }

    // Time spent waiting on the async legend measurement before layout can apply.
    const layoutGateStartedAtMs = performance.now();
    const layoutProps = await host.layoutGate.wait();
    if (!isCurrent()) return;
    Tracing.log("sheet/layout-gate-wait", {
      ms: performance.now() - layoutGateStartedAtMs,
      sheetId: sheet.handle.network.id,
    });
    // Coalesce the post-resolved layout changes to ONE applyLayout per frame.
    // A single resize fires the gate twice synchronously — setViewportWidth
    // (onSizeChange) and setRightOccludedPx (sidebarWidthInvalidated) — which
    // would otherwise run two $$.mutate(applyLayout) → two window.resets + two
    // emit.all(true) in one frame. That double rebind is what races
    // assertChildren into hiding pending-bind columns (the resize flicker), and
    // it is redundant work. Merge at the handler (the gate stays a dumb
    // accumulator); closure-local state is fresh per bindSheet, so no flag
    // leaks across navigation.
    let layoutScheduled = false;
    let latestLayout: Option<SheetLayoutGate.Props> = null;
    host.layoutGate.onChange = (p) => {
      if (!isCurrent()) return;
      latestLayout = p;
      if (layoutScheduled) return;
      layoutScheduled = true;
      $$.mutate(() => {
        layoutScheduled = false;
        if (!isCurrent()) return;
        applyLayout(nonnull(latestLayout));
        host.core.element.toggleAttribute("legend-ready", true);
        layout.rebindFixedChildPositions();
        host.rebindFixedChildYPositions();
      });
    };
    // A view-config flip is the SAME sheet under a new renderer, so the window
    // keeps its position and `bindLayout`'s translated re-anchor is the
    // authority. Only a real sheet switch anchors at the origin. This used to
    // read `navigator.currentIndex()` here and pass it through — but "here" is
    // before `applyLayout`, so the number was in the OUTGOING index space;
    // `bindContent` now reads it after the geometry rebind instead.
    const indexAnchor = index != null
      ? SheetLayout.IndexAnchor.at(index)
      : options.sheetIdChanged || options.anchorChanged
      ? SheetLayout.IndexAnchor.at(0)
      : SheetLayout.IndexAnchor.keep();

    await new Promise<void>((resolve, reject) => {
      $$.mutate(() => {
        if (!isCurrent()) {
          resolve();
          return;
        }
        // Only suspend after all asynchronous layout prerequisites completed.
        // The existing bracket stays open through the queued bind drain and
        // keeps the focused editor listening throughout the sheet switch.
        const releaseObservers = options.sheetChanged ? host.holdEditorObservers(bindSignal) : null;
        try {
          layout.presentation.setBodyMarginTop(SheetElementLayout.bodyMarginTop(sheet));
          layout.presentation.setFooterHeight(
            sheet.layout.footerRowsHeightPx + 2,
          );
          host.applyResolvedLayout(sheet, layout, layoutProps, {
            indexAnchor,
            animate: options.animate,
            sheetId: sheet.handle.network.id,
            contextDirtied: options.contextDirtied,
            skipScroll: options.skipScroll,
          });
          host.root.scrollPort.headerPeek.setHeight(
            SheetElementLayout.headerHeight(sheet),
            false,
          );
          // On a switch to another sheet the reveal is gated below on the
          // drain settling — revealing here shows an empty grid for a frame
          // and then columns popping in one by one as the (budgeted) drain
          // reaches them. A same-sheet pass, a view-config switch included,
          // keeps the immediate reveal: its columns are already covered
          // per-column by their `pending-layout` marks. It asks the
          // same question `bindContentVisibility` does: whether the
          // presentation conceals the content (a failed one does not).
          if (!options.sheetIdChanged && !host.core.presentation.concealing) {
            host.core.element.toggleAttribute("legend-ready", true);
            host.root.scrollPort.toggleContentVisibility(true, true);
          }
          if (!options.skipScroll) {
            // Deferred one frame, into the read phase. A scrollTop write forces a
            // synchronous style+layout flush of whatever is already dirty; issued
            // here — between the presentation writes / applyLayout above and
            // bindContent below — it pays that flush against a fully dirtied tree
            // (a ~230ms forced recalc inside the switch's worst task, run30
            // trace). Next frame's read phase sees clean style (this frame's
            // natural recalc has run, nothing has re-dirtied yet), so the same
            // write is near-free. The deferral must NOT be shortened to "top of
            // this mutate": the clamp would then run against the OLD content
            // height (bindScrollProps applies the new one below) and poison the
            // setter's skip-cache. One frame of deferral is invisible: every
            // skipScroll=false path either holds content hidden behind the
            // drain-gated reveal (full switch) or restores an unchanged value
            // (the setter no-ops). The generation guard drops the restore when a
            // newer full rebind superseded this bind while the frame turned.
            const scrollGen = host.core.bindState.bindGeneration;
            $$.measureNext(() => {
              if (!isCurrent() || host.core.bindState.bindGeneration !== scrollGen) return;
              host.root.scrollPort.setListScrollTop(
                sheet.layout.initialScrollTop,
              );
            });
          }

          layout.bindScrollProps({
            headerHeight: SheetElementLayout.headerHeight(sheet),
            scrollContentHeight: sheet.layout.bodyHeightPx,
            footerHeight: sheet.layout.footerRowsHeightPx || null,
            canShowDebugPopover: host.shouldShowDebugPopover,
          });

          // The epoch starts before the asynchronous legend/layout gates, but
          // content may run only after this matching incoming pass installs.
          // A throw leaves the barrier held for explicit retry/supersession.
          if (isCurrent()) host.root.scrollPort.commitBindGeneration(revealGen);

          resolve();
        } catch (error) {
          reject(error);
        } finally {
          releaseObservers?.();
        }
      });
    });

    if (!isCurrent()) return;
    // The geometry the rendered columns are NOW painting. `rebindStructurePass`
    // diffs the next pass's geometry against this to decide whether its co-hide
    // is protecting against anything (see `StructuralCoHide`); it is captured
    // here, after the mutate, because that is the moment the DOM and this
    // snapshot agree.
    host.committed.committedRows = rowGeometryOf(sheet);

    // Settled header shape after a bind commits (height, epoch, legend revealed);
    // pairs with `scroll/header-violation` to diagnose a blank header band.
    Tracing.log("header/health", {
      sheetId: sheet.handle.network.id,
      gen: host.core.bindState.bindGeneration,
      headerHeight: SheetElementLayout.headerHeight(sheet),
      legendReady: host.core.element.hasAttribute("legend-ready"),
    });
}
